import { line } from 'd3-shape'
import LineAreaAdjuster from './line-area-adjuster'

class LineAdjuster extends LineAreaAdjuster {

  constructor (options) {
    super(options)

    this.pointBasedPathGenerator = line().x(d => d.x)
                                         .y(d => d.y1)
                                         .defined(this.defined)
                                         .curve(this.curve)
  }

  /*
   * Returns a path `d` generator that creates a line
   */
  _pathGenerator (xScale, yScale) {
    return line().x((d, i) => xScale(this.chart.xAccessor(d, i), i))
                 .y((d, i) => yScale(d._range[1], d))
                 .defined(this.defined)
                 .curve(this.curve)
  }

  updateDrawing (selection, Curve, scalers) {
    let generator = this.pathGenerator

    if (Curve) {
      scalers = scalers || {
        x: this.previous.scalers.x.center,
        y: this.previous.yScale
      }
      generator = this._pathGenerator(scalers.x, scalers.y)
                      .curve(context => new Curve(context))
    }
    selection.selectAll('path.line')
             .attr('d', generator)
  }

  /*
   * Inserts the line path into the DOM. By default uses the default
   * path generator. If `scalers`` is defined, it will generate a path using
   * the set scalers.x and scalers.y
   */
  insert (selection, scalers) {
    let generator = this.pathGenerator

    if (scalers) {
      generator = this._pathGenerator(scalers.x, scalers.y)
    }

    selection.selectAll('path.line')
             .data(d => [d.points, d.points]) // Draw two lines
             .enter()
              .append('path')
               .attr('d', generator)
               .attr('class', (d, i) => {
                 let clazzes = ['line']
                 if (i === 0) {
                   clazzes.push('outer-line') // Used for opacity when lines intersect on a graph. Creates an outer shell around the main data-line
                 } else if (i === 1) {
                   clazzes.push('data-line') // This is the main data line the user can see
                 }
                 return clazzes.join(' ')
               })
  }

  rebind (context, points) {
    let selection = context.selection ? context.selection() : context

    selection.selectAll('path.line')
             .data([points, points]) // We need two paths for line charts.

    context.selectAll('path.line')
           .attr('d', this.pathGenerator) // And redraw the path with the actual data so it's properly trimmed
  }

  updatePathWithPoints (selection, points) {
    selection.selectAll('path.line')
             .attr('d', this.pointBasedPathGenerator(points)) // Update the line path w/ the new control points
  }

  transitionPaths (points, selection, merged, xScale0, xScale1) {
    // Redraw the line in case it needs to be extended based on the adjusted scale0
    selection.selectAll('path.line')
             .attr('d', this._pathGenerator(xScale0, this.previous.yScale)(points))

    // Now transition to the new path using scale1
    this.transitionPathTo(points, merged, xScale1, this.yScale)
  }

  transitionPathTo (points, merged, xScale, yScale) {
    merged.selectAll('path.line')
          .attr('d', this._pathGenerator(xScale, yScale)(points))
  }

  /*
   * Looks at the points within the selection and builds multiple
   * `path` elements where there is a disconnect in the line. We need this for transitioning
   * See #443. This allows us to transition over each of the subpaths that make up the line
   *
   * Passing `intialOffset` allows us to control if the subpaths are hidden or visible by default.
   * Pass `scalers` to change the scalers used on the path generator. Used if a subpath is created on an old line
   */
  insertSubPaths (selection, initialOffset, scalers) {
    let points = selection.datum().points
      , generator = this.pathGenerator
      , subPaths = []
      , start = 0
      , i = 0

    // Determine an array of array, where each element in the array contains all the points
    // for that individual path.
    while (i < points.length) {
      if (isNaN(points[i].raw)) {
        // We have a disconnect, so build a new path
        subPaths.push(points.slice(start, i))
        start = i
      }
      i++
    }
    subPaths.push(points.slice(start, i))

    // Remove subpaths that aren't a line between two points
    subPaths = subPaths.filter(path => path.length > 1)

    if (scalers) {
      // We need a new path generator
      generator = this._pathGenerator(scalers.x, scalers.y)
    }

    selection.selectAll('path.line.line-subpath')
             .data(subPaths)
             .enter()
             .insert('path', ':first-child') // So they are behind the `points`
             .attr('class', 'line line-subpath')
             .attr('d', generator)
             .attr('stroke-dasharray', function () {
               let length = this.getTotalLength()
               return `${length} ${length}`
             })
             .attr('stroke-dashoffset', function () {
               return isNaN(initialOffset) ? this.getTotalLength() : initialOffset
             })
             .sort() // Put them in order based on the subPaths data. They are out of order since we're using `:first-child`
  }

}

export default LineAdjuster
